import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { useApp } from '@/contexts/AppContext';
import { generateReportPDF, downloadCSV } from '@/utils/pdfUtils';
import {
  demoBookings,
  demoPayments,
  demoRooms,
  demoShifts,
  demoUsers,
  demoActivityLogs,
  demoCanteenBills,
} from '@/mocks/demoData';

const COLORS = ['#1a2b4a', '#c9a84c', '#22c55e', '#ef4444', '#f59e0b', '#6b7280', '#8b5cf6', '#ec4899'];

export default function ReceptionReports() {
  const { addToast } = useApp();
  const [dateFrom, setDateFrom] = useState('2026-05-01');
  const [dateTo, setDateTo] = useState('2026-05-15');

  const fromDate = new Date(dateFrom);
  const toDate = new Date(dateTo);

  const bookingsInRange = useMemo(() => {
    return demoBookings.filter((b) => {
      const d = new Date(b.created_at);
      return d >= fromDate && d <= toDate;
    });
  }, [fromDate, toDate]);

  const paymentsInRange = useMemo(() => {
    return demoPayments.filter((p) => {
      const d = new Date(p.created_at);
      return d >= fromDate && d <= toDate;
    });
  }, [fromDate, toDate]);

  const dailyBookingData = useMemo(() => {
    const map = new Map<string, number>();
    bookingsInRange.forEach((b) => {
      const d = new Date(b.created_at).toISOString().slice(0, 10);
      map.set(d, (map.get(d) || 0) + 1);
    });
    return Array.from(map.entries()).map(([date, count]) => ({ date, count })).sort((a, b) => a.date.localeCompare(b.date));
  }, [bookingsInRange]);

  const shiftCollectionData = useMemo(() => {
    return demoShifts.map((s) => ({
      name: s.name,
      value: s.collections,
    }));
  }, []);

  const revenueData = useMemo(() => {
    const roomRevenue = paymentsInRange.reduce((s, p) => s + p.amount, 0);
    const canteenRevenue = demoCanteenBills
      .filter((b) => {
        const d = new Date(b.created_at);
        return d >= fromDate && d <= toDate && b.status === 'paid';
      })
      .reduce((s, b) => s + b.total, 0);
    return [
      { name: 'Room Revenue', value: roomRevenue },
      { name: 'Canteen Revenue', value: canteenRevenue },
    ];
  }, [paymentsInRange, fromDate, toDate]);

  const pendingData = useMemo(() => {
    return demoBookings.filter((b) => b.pending_amount > 0);
  }, []);

  const occupancyData = useMemo(() => {
    const total = demoRooms.length;
    const occupied = demoRooms.filter((r) => r.status === 'occupied').length;
    return [
      { name: 'Occupied', value: occupied, rate: Math.round((occupied / total) * 100) },
      { name: 'Available', value: total - occupied, rate: Math.round(((total - occupied) / total) * 100) },
    ];
  }, []);

  const receptionistPerf = useMemo(() => {
    return demoUsers.filter((u) => u.role === 'receptionist').map((u) => {
      const bookings = demoBookings.filter((b) => {
        const log = demoActivityLogs.find((l) => l.user_id === u.id && l.entity_id === b.id);
        return !!log;
      }).length;
      const revenue = paymentsInRange.filter((p) => p.created_by === u.id).reduce((s, p) => s + p.amount, 0);
      return { name: u.full_name, bookings, revenue };
    });
  }, [paymentsInRange]);

  const handleGeneratePDF = (title: string, getRows: () => Record<string, string | number>[], getCols: () => { header: string; dataKey: string }[]) => {
    const rows = getRows();
    const cols = getCols();
    if (rows.length === 0) {
      addToast('error', 'No data in selected date range');
      return;
    }
    generateReportPDF(title, cols, rows, `Period: ${dateFrom} to ${dateTo}`);
    addToast('success', `${title} PDF downloaded`);
  };

  const handleDownloadCSV = (title: string, getRows: () => Record<string, string | number>[], getCols: () => { header: string; key: string }[]) => {
    const rows = getRows();
    const cols = getCols();
    if (rows.length === 0) {
      addToast('error', 'No data in selected date range');
      return;
    }
    downloadCSV(title.replace(/\s+/g, '-'), cols, rows);
    addToast('success', `${title} CSV downloaded`);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Reception Reports</h1>
        <p className="text-navy-500 mt-1">PDF and Excel reports for reception operations.</p>
      </div>

      {/* Date Filter */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-navy-700 mb-1.5">From Date</label>
            <input type="date" className="px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-700 mb-1.5">To Date</label>
            <input type="date" className="px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Daily Bookings Chart */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Daily Bookings</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Daily Room Booking Report', () => dailyBookingData.map((d) => ({ date: d.date, bookings: d.count })), () => [{ header: 'Date', dataKey: 'date' }, { header: 'Bookings', dataKey: 'bookings' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Daily Room Booking Report', () => dailyBookingData.map((d) => ({ date: d.date, bookings: d.count })), () => [{ header: 'Date', key: 'date' }, { header: 'Bookings', key: 'bookings' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyBookingData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="count" fill="#1a2b4a" radius={[4, 4, 0, 0]} maxBarSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Shift Collections */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Shift Collections</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Shift-wise Collection Report', () => demoShifts.map((s) => ({ shift: s.name, collections: s.collections, opening: s.opening_balance, closing: s.closing_balance })), () => [{ header: 'Shift', dataKey: 'shift' }, { header: 'Opening', dataKey: 'opening' }, { header: 'Collections', dataKey: 'collections' }, { header: 'Closing', dataKey: 'closing' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Shift-wise Collection Report', () => demoShifts.map((s) => ({ shift: s.name, collections: s.collections, opening: s.opening_balance, closing: s.closing_balance })), () => [{ header: 'Shift', key: 'shift' }, { header: 'Opening', key: 'opening' }, { header: 'Collections', key: 'collections' }, { header: 'Closing', key: 'closing' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={shiftCollectionData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                  {shiftCollectionData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} formatter={(v: number) => `Rs. ${v.toLocaleString('en-IN')}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {shiftCollectionData.map((item, i) => (
              <div key={item.name} className="flex items-center gap-1 text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                <span className="text-navy-600">{item.name}</span>
                <span className="font-semibold text-navy-800">Rs. {item.value.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Occupancy */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Occupancy Overview</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Occupancy Report', () => occupancyData.map((d) => ({ status: d.name, rooms: d.value, percentage: `${d.rate}%` })), () => [{ header: 'Status', dataKey: 'status' }, { header: 'Rooms', dataKey: 'rooms' }, { header: 'Percentage', dataKey: 'percentage' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Occupancy Report', () => occupancyData.map((d) => ({ status: d.name, rooms: d.value, percentage: `${d.rate}%` })), () => [{ header: 'Status', key: 'status' }, { header: 'Rooms', key: 'rooms' }, { header: 'Percentage', key: 'percentage' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={occupancyData} cx="50%" cy="50%" outerRadius={70} paddingAngle={3} dataKey="value" label={({ name, rate }) => `${name}: ${rate}%`}>
                  <Cell fill="#ef4444" />
                  <Cell fill="#22c55e" />
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Revenue Breakdown</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Revenue Report', () => revenueData.map((d) => ({ source: d.name, amount: d.value })), () => [{ header: 'Source', dataKey: 'source' }, { header: 'Amount (Rs.)', dataKey: 'amount' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Revenue Report', () => revenueData.map((d) => ({ source: d.name, amount: d.value })), () => [{ header: 'Source', key: 'source' }, { header: 'Amount (Rs.)', key: 'amount' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={120} />
              <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} formatter={(v: number) => [`Rs. ${v.toLocaleString('en-IN')}`, '']} />
              <Bar dataKey="value" fill="#1a2b4a" radius={[0, 4, 4, 0]} maxBarSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pending Payments & Receptionist Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Pending Payments */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Pending Payments ({pendingData.length})</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Pending Payments Report', () => pendingData.map((b) => ({ booking: b.booking_id, guest_id: b.guest_id, pending: b.pending_amount, total: b.total_amount })), () => [{ header: 'Booking ID', dataKey: 'booking' }, { header: 'Guest ID', dataKey: 'guest_id' }, { header: 'Total', dataKey: 'total' }, { header: 'Pending', dataKey: 'pending' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Pending Payments Report', () => pendingData.map((b) => ({ booking: b.booking_id, guest_id: b.guest_id, pending: b.pending_amount, total: b.total_amount })), () => [{ header: 'Booking ID', key: 'booking' }, { header: 'Guest ID', key: 'guest_id' }, { header: 'Total', key: 'total' }, { header: 'Pending', key: 'pending' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pendingData.map((b, i) => ({ name: `B${i + 1}`, pending: b.pending_amount, total: b.total_amount }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} formatter={(v: number) => [`Rs. ${v.toLocaleString('en-IN')}`, '']} />
                <Bar dataKey="total" fill="#e5e7eb" radius={[4, 4, 0, 0]} maxBarSize={24} />
                <Bar dataKey="pending" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-navy-200" /> Total</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-500" /> Pending</span>
          </div>
        </div>

        {/* Receptionist Performance */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Receptionist Performance</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Receptionist Performance', () => receptionistPerf.map((p) => ({ name: p.name, bookings: p.bookings, revenue: p.revenue })), () => [{ header: 'Name', dataKey: 'name' }, { header: 'Bookings', dataKey: 'bookings' }, { header: 'Revenue (Rs.)', dataKey: 'revenue' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Receptionist Performance', () => receptionistPerf.map((p) => ({ name: p.name, bookings: p.bookings, revenue: p.revenue })), () => [{ header: 'Name', key: 'name' }, { header: 'Bookings', key: 'bookings' }, { header: 'Revenue (Rs.)', key: 'revenue' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={receptionistPerf}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
                <Bar yAxisId="left" dataKey="bookings" fill="#1a2b4a" radius={[4, 4, 0, 0]} maxBarSize={24} />
                <Bar yAxisId="right" dataKey="revenue" fill="#c9a84c" radius={[4, 4, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-navy-800" /> Bookings</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gold-500" /> Revenue</span>
          </div>
        </div>
      </div>

      {/* Activity Audit Trail */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Activity Audit Trail</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Activity Audit Trail', () => demoActivityLogs.slice(0, 20).map((l) => ({ date: new Date(l.created_at).toLocaleString('en-IN'), user: l.user_name, action: l.action, entity: l.entity_type, id: l.entity_id })), () => [{ header: 'Date', dataKey: 'date' }, { header: 'User', dataKey: 'user' }, { header: 'Action', dataKey: 'action' }, { header: 'Entity', dataKey: 'entity' }, { header: 'ID', dataKey: 'id' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Activity Audit Trail', () => demoActivityLogs.slice(0, 20).map((l) => ({ date: new Date(l.created_at).toLocaleString('en-IN'), user: l.user_name, action: l.action, entity: l.entity_type, id: l.entity_id })), () => [{ header: 'Date', key: 'date' }, { header: 'User', key: 'user' }, { header: 'Action', key: 'action' }, { header: 'Entity', key: 'entity' }, { header: 'ID', key: 'id' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Date</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">User</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Action</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Entity</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">ID</th>
              </tr>
            </thead>
            <tbody>
              {demoActivityLogs.slice(0, 8).map((log) => (
                <tr key={log.id} className="border-t border-navy-100 hover:bg-navy-50/50">
                  <td className="px-4 py-2.5 text-navy-500 text-xs">{new Date(log.created_at).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2.5 text-navy-700 text-xs font-medium">{log.user_name}</td>
                  <td className="px-4 py-2.5 text-navy-700 text-xs">{log.action}</td>
                  <td className="px-4 py-2.5 text-navy-500 text-xs capitalize">{log.entity_type.replace('_', ' ')}</td>
                  <td className="px-4 py-2.5 text-navy-500 text-xs">{log.entity_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{bookingsInRange.length}</div>
          <div className="text-xs text-navy-500 mt-1">Bookings in Range</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">Rs. {paymentsInRange.reduce((s, p) => s + p.amount, 0).toLocaleString('en-IN')}</div>
          <div className="text-xs text-navy-500 mt-1">Payments in Range</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{Math.round((demoRooms.filter((r) => r.status === 'occupied').length / demoRooms.length) * 100)}%</div>
          <div className="text-xs text-navy-500 mt-1">Current Occupancy</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{demoActivityLogs.length}</div>
          <div className="text-xs text-navy-500 mt-1">Total Activities</div>
        </div>
      </div>
    </div>
  );
}